// Fetch client for the Operations Console API.
//
// Mirrors the vendor-portal contract: the access token lives IN MEMORY and
// rides as `Authorization: Bearer` (so it never sits in storage/XSS reach); the
// refresh token is an httpOnly cookie the browser sends automatically. On a 401
// we single-flight one refresh against `/console/auth/refresh` and retry. The
// authenticated console endpoints are Bearer-authed (CSRF-exempt), so no CSRF
// token is needed.

const API_BASE = "/api/v1/console";

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}
export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function buildInit(init: RequestInit): RequestInit {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  return { ...init, headers, credentials: "include" };
}

let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    if (!res.ok) return false;
    const body = await res.json().catch(() => null);
    const token = body?.data?.access_token ?? body?.access_token;
    if (typeof token === "string" && token) {
      accessToken = token;
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const msg =
      body?.error?.message || body?.error || body?.detail || body?.message || res.statusText;
    throw new ApiError(res.status, typeof msg === "string" ? msg : "Request failed");
  }
  // Endpoints wrap payloads as { success, data, meta }; fall back to the raw body.
  return (body?.data ?? body) as T;
}

export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await fetch(`${API_BASE}${path}`, buildInit(init));
  if (res.status === 401) {
    const ok = await refreshSession();
    if (ok) res = await fetch(`${API_BASE}${path}`, buildInit(init));
  }
  return unwrap<T>(res);
}

export const apiGet = <T = unknown>(path: string) => api<T>(path, { method: "GET" });
export const apiPost = <T = unknown>(path: string, body?: unknown) =>
  api<T>(path, { method: "POST", body: body === undefined ? "{}" : JSON.stringify(body) });
